'use client';

import { createRef, useCallback, useEffect, useMemo, useRef } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';

import debounce from 'lodash.debounce';

import { DataLayerWhere } from '@/modules/cart/services/data-layer-events/add-to-cart';
import { Spinner } from '@/modules/common/components/icons';
import { useAppSelector } from '@/modules/common/hooks/useStore';
import { useUiKey } from '@/modules/common/hooks/useUiKey';
import { urlSearchParamsToQueryString } from '@/modules/common/lib/utils';
import { ALGOLIA_SPACE_APPLICATION_FACET } from '@/modules/common/services/algoliaConstants';
import { getLastCategoryName } from '@/modules/common/services/algoliaUtils';
import { selectSearchMethod } from '@/modules/common/state/dataLayerSlice';
import { useFinder } from '@/modules/cora/context/FinderProvider';
import { FiltersModalTrigger } from '@/modules/product/components/modal/FiltersModal';
import SampleDepartmentList from '@/modules/product/components/samples/SampleDepartmentList';
import { SamplesCurrentRefinementsSimplified } from '@/modules/product/components/samples/SamplesCurrentRefinementsSimplified';
import { SamplesLeftSidebarSimplified } from '@/modules/product/components/samples/SamplesLeftSidebarSimplified';
import { useCategoryContext } from '@/modules/product/context/CategoryContext';
import { SearchPageConfigFragment } from '@/modules/product/gql/__generated/searchPage.generated';
import { useMapAlgoliaProductToClientMulti } from '@/modules/product/hooks/useProductTransform';
import { useSearchCache } from '@/modules/product/hooks/useSearchCache';
import { useSearchPageQuery } from '@/modules/product/hooks/useSearchPageQuery';
import { useVisibleItemsTracking } from '@/modules/product/hooks/useVisibleItemsTracking';
import { ProductPdpInterface } from '@/modules/product/models/product-pdp.interface';
import { applyFilterOverrides } from '@/modules/product/services/applyFilterOverrides';
import { DataLayerFeature } from '@/modules/product/services/data-layer-events/add-to-wishlist';
import { viewSearchResultsDataLayerEvent } from '@/modules/product/services/data-layer-events/view-search-results';
import { ApiToFiltersSchema } from '@/modules/product/services/filtersTransform';
import { parseFiltersFromUrl } from '@/modules/product/services/routingHelpers';
import { SampleCardSkeleton } from '@/modules/sample-card';
import { SampleCard } from '@/modules/sample-card';
import { selectShoppingListProducts } from '@/modules/shopping-list/state/shoppingListSlice';

import { centsToRoundDollars } from '@/utils/formatPrice';

import { CollectionResultCarouselSimplified } from '../components/AlgoliaSearch/CollectionResultCarouselSimplified';
import { RefineSearchButtonsSimplified } from '../components/search/RefineSearchButtonsSimplified';
import { SERPSearchBarSimplified } from '../components/search/SERPSearchBarSimplified';
import { AlgoliaCollection, AlgoliaToCollectionSchema } from '../services/collectionTransform';
import { ALGOLIA_SEARCH_INDEX } from '../services/consts';

// Fallbacks when the SearchPage CMS entry is missing or a field is empty.
const DEFAULTS = {
  pageTitle: 'All Samples',
  searchHeadingTemplate: 'Search for "[query]"',
  loadingResultsLabel: 'Loading results…',
  resultsCountTemplate: '[count] results found',
  resultsRefinePrompt: 'Narrow with filters or add more details.',
  noResultsTitle: 'Sorry, no results found',
  noResultsDescription: 'Here are some relevant categories you might like.',
} as const;

const REFINE_PROMPT_MIN_RESULTS = 12;

type SearchPageTemplateSimplifiedProps = {
  searchPageConfig?: SearchPageConfigFragment | null;
};

export default function SearchPageTemplateSimplified({
  searchPageConfig,
}: SearchPageTemplateSimplifiedProps = {}) {
  const { filtersDefinition } = useFinder();
  const searchParams = useSearchParams();
  const router = useRouter();
  const uiKey = useUiKey();
  const { hasCacheItems } = useSearchCache();
  const searchMethod = useAppSelector(selectSearchMethod);
  const shoppingListProducts = useAppSelector(selectShoppingListProducts);

  const {
    samplesResults,
    collectionResults,
    isLoading,
    isLastPage,
    loadMore,
    facetsForFilters,
    renderingContentForFilters,
  } = useSearchPageQuery();

  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Filter list priority: SearchPage singleton → current department → global default.
  // Filter overrides follow the same cascade (label/sort tweaks scoped to this page).
  const { department: currentDepartment } = useCategoryContext();
  const mappedFilters = useMemo(() => {
    const baseFilters = (() => {
      const searchPageItems = searchPageConfig?.availableFiltersCollection?.items;
      if (searchPageItems?.length) {
        return searchPageItems
          .filter((item): item is NonNullable<typeof item> => item !== null)
          .map((facet) => ApiToFiltersSchema.parse(facet));
      }
      const departmentItems = currentDepartment?.availableFiltersCollection?.items;
      if (departmentItems?.length) {
        return departmentItems.map((facet) => ApiToFiltersSchema.parse(facet));
      }
      return undefined;
    })();

    if (!baseFilters) {
      return undefined;
    }

    const overrides =
      searchPageConfig?.filterOverridesCollection?.items?.length ?
        searchPageConfig.filterOverridesCollection.items
      : currentDepartment?.filterOverridesCollection?.items;

    return applyFilterOverrides(baseFilters, overrides);
  }, [searchPageConfig, currentDepartment]);

  // Parse current filters from URL for display
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params;
  }, [searchParams]);

  const routeState = useMemo(() => {
    return parseFiltersFromUrl({
      queryParams,
      filtersDefinition,
      separator: '|',
    });
  }, [queryParams, filtersDefinition]);

  // Build attribute to label map early so it can be used by currentFiltersFromContext.
  const attributeToLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    filtersDefinition.forEach((def) => {
      if (def.attribute && def.label) {
        map.set(def.attribute, def.label);
      }
    });
    return map;
  }, [filtersDefinition]);

  // Convert routeState to currentFilters format for SamplesCurrentRefinements
  const currentFiltersFromContext = useMemo(() => {
    const filters: Array<{ name: string; attribute: string; value: string; action: () => void }> =
      [];

    filtersDefinition.forEach(({ slug, attribute, label: _label }) => {
      const refinementValue = routeState.refinements[slug];
      const rangeValue = routeState.range[slug];

      if (refinementValue && Array.isArray(refinementValue)) {
        refinementValue.forEach((value) => {
          filters.push({
            name: getLastCategoryName(value),
            attribute,
            value,
            action: () => {
              const params = new URLSearchParams(searchParams.toString());
              const currentValues = params.get(slug)?.split('|') || [];
              const newValues = currentValues.filter((v) => v !== value);
              if (newValues.length > 0) {
                params.set(slug, newValues.join('|'));
              } else {
                params.delete(slug);
              }
              params.delete('page');
              router.push(`/search?${urlSearchParamsToQueryString(params)}`);
            },
          });
        });
      }

      if (rangeValue) {
        const removeRangeAction = () => {
          const params = new URLSearchParams(searchParams.toString());
          params.delete(slug);
          params.delete('page');
          router.push(`/search?${urlSearchParamsToQueryString(params)}`);
        };

        // Build a single chip showing "Retail Price: $0 - $25"
        // Only include bounds that differ from the absolute min/max (if known).
        const minDollars = centsToRoundDollars(rangeValue.min ?? 0);
        const maxDollars = centsToRoundDollars(rangeValue.max ?? 0);
        const filterLabel = attributeToLabelMap.get(attribute) || attribute;
        const name = `${filterLabel}: $${minDollars} - $${maxDollars}`;

        filters.push({
          name,
          attribute,
          value: `${rangeValue.min ?? ''}:${rangeValue.max ?? ''}`,
          action: removeRangeAction,
        });
      }
    });

    return filters;
  }, [routeState, filtersDefinition, searchParams, attributeToLabelMap, router]);

  const searchQuery = queryParams.query ?? '';

  const pageHeading = useMemo(() => {
    const q = searchQuery.trim();
    if (!q) {
      return searchPageConfig?.pageTitle || DEFAULTS.pageTitle;
    }
    const formattedQuery = q.charAt(0).toUpperCase() + q.slice(1);
    const template = searchPageConfig?.searchHeadingTemplate || DEFAULTS.searchHeadingTemplate;
    return template.replace('[query]', formattedQuery);
  }, [searchQuery, searchPageConfig?.pageTitle, searchPageConfig?.searchHeadingTemplate]);

  const totalNbHits = samplesResults?.nbHits || 0;
  const shouldShowRefinePrompt = totalNbHits >= REFINE_PROMPT_MIN_RESULTS;

  const resultsCountText = useMemo(() => {
    const template = searchPageConfig?.resultsCountTemplate || DEFAULTS.resultsCountTemplate;
    return template.replace('[count]', totalNbHits.toLocaleString('en-US'));
  }, [searchPageConfig?.resultsCountTemplate, totalNbHits]);

  const loadingResultsLabel = searchPageConfig?.loadingResultsLabel || DEFAULTS.loadingResultsLabel;
  const resultsRefinePrompt = searchPageConfig?.resultsRefinePrompt || DEFAULTS.resultsRefinePrompt;
  const noResultsTitle = searchPageConfig?.noResultsTitle || DEFAULTS.noResultsTitle;
  const noResultsDescription =
    searchPageConfig?.noResultsDescription || DEFAULTS.noResultsDescription;

  // Transform Algolia hits to products
  const algoliaProducts = useMapAlgoliaProductToClientMulti(samplesResults?.hits || []);

  const products = useMemo(() => {
    return algoliaProducts.filter(
      (product) => product && product.published
    ) as ProductPdpInterface[];
  }, [algoliaProducts]);

  // Transform collection hits
  const collections = useMemo(() => {
    if (!collectionResults?.hits) {
      return [];
    }
    return collectionResults.hits
      .map((hit) => {
        try {
          return AlgoliaToCollectionSchema.parse(hit) as AlgoliaCollection;
        } catch {
          return null;
        }
      })
      .filter((c): c is AlgoliaCollection => c !== null);
  }, [collectionResults]);

  const isZeroResults = !totalNbHits && !isLoading;

  // Build applied_filters once so it can be reused across all analytics call-sites.
  const applied_filters = useMemo(
    () =>
      currentFiltersFromContext.map((item) => ({
        type: attributeToLabelMap.get(item.attribute) || item.attribute,
        value: item.name,
      })),
    [currentFiltersFromContext, attributeToLabelMap]
  );

  const productRefs = useMemo(() => products.map(() => createRef<HTMLDivElement>()), [products]);

  useVisibleItemsTracking({
    products,
    productRefs,
    shoppingListProducts,
    feature: DataLayerFeature.Search,
    where: DataLayerWhere.Search,
    itemListId: ALGOLIA_SEARCH_INDEX,
    itemListName: 'Search results',
    applied_filters,
    searchTestId: samplesResults?.abTestID,
    searchTestVariantId: samplesResults?.abTestVariantID,
  });

  const fireViewSearchResults = useCallback(() => {
    if (samplesResults?.queryID && samplesResults?.query) {
      viewSearchResultsDataLayerEvent({
        query_id: samplesResults.queryID,
        search_term: samplesResults.query,
        method: searchMethod,
        filtered: applied_filters.length > 0,
        results: samplesResults.nbHits || 0,
        applied_filters,
        searchTestId: samplesResults.abTestID,
        searchTestVariantId: samplesResults.abTestVariantID,
      });
    }
  }, [
    samplesResults?.queryID,
    samplesResults?.query,
    samplesResults?.nbHits,
    samplesResults?.abTestID,
    samplesResults?.abTestVariantID,
    searchMethod,
    applied_filters,
  ]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedViewSearchResults = useCallback(
    debounce(() => {
      fireViewSearchResults();
    }, 1500),
    [fireViewSearchResults]
  );

  useEffect(() => {
    if (samplesResults?.queryID && samplesResults?.query) {
      debouncedViewSearchResults();
    }
    return () => {
      debouncedViewSearchResults.cancel();
    };
  }, [debouncedViewSearchResults, samplesResults?.queryID, samplesResults?.query]);

  useEffect(() => {
    const node = loadMoreRef.current;
    const displayCount = products.length;
    if (!node || displayCount === 0 || isLastPage || isLoading) {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !isLastPage) {
        loadMore();
      }
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [products.length, loadMore, isLastPage, isLoading]);

  return (
    <>
      {!isZeroResults && (
        <div className="app-container space-y-3 border-gray-100 pb-3 pt-4 lg:border-b-2 lg:py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="mb-1.5 text-xl font-semibold leading-6 text-gray-900 md:leading-8 lg:text-2xl"
                data-testid="page-title"
              >
                {pageHeading}
              </h1>

              <p className="text-xs" data-testid="results-refine-label">
                {isLoading && totalNbHits === 0 ?
                  <span data-testid="sub-title" className="text-gray-500">
                    {loadingResultsLabel}
                  </span>
                : <>
                    <span data-testid="sub-title" className="mb-1 block lg:inline">
                      {resultsCountText}
                      {shouldShowRefinePrompt && '.'}
                    </span>{' '}
                    {shouldShowRefinePrompt && (
                      <span className="text-gray-500">{resultsRefinePrompt}</span>
                    )}
                  </>
                }
              </p>
            </div>

            <div className="flex items-center lg:hidden">
              <hr className="h-4 w-[1px] bg-gray-100" />
              <FiltersModalTrigger
                where={DataLayerWhere.Search}
                useSimplifiedFilters
                currentItems={currentFiltersFromContext}
                facets={facetsForFilters}
                mappedFilters={mappedFilters}
              />
            </div>
          </div>

          <SERPSearchBarSimplified className="lg:hidden" testIdPrefix="serp-top" />

          <SamplesCurrentRefinementsSimplified
            currentItems={currentFiltersFromContext}
            feature={DataLayerFeature.Search}
            where={DataLayerWhere.TopOfList}
            hiddenAttributes={[ALGOLIA_SPACE_APPLICATION_FACET]}
          />
        </div>
      )}

      <div className="mx-auto w-full transition-all ease-in-out md:flex md:max-w-[1920px]">
        <aside className="absolute opacity-0 md:sticky md:top-header-gap-unwrapped md:z-10 md:h-content-full-height-unwrapped md:w-plp-left-sidebar-gap md:shrink-0 md:self-start md:overflow-y-auto md:border-r md:border-gray-100 md:bg-white md:opacity-100">
          <SamplesLeftSidebarSimplified
            facets={facetsForFilters}
            renderingContent={renderingContentForFilters}
            mappedFilters={mappedFilters}
          />
        </aside>
        <section className="relative z-0 w-full md:block">
          {isLoading && !hasCacheItems ?
            <div className="grid h-fit grid-cols-2 gap-x-4 gap-y-4 md:grid-cols-[repeat(auto-fill,minmax(185px,1fr))] md:gap-y-8 lg:grid-cols-[repeat(auto-fill,minmax(262px,1fr))]">
              {Array.from({ length: 20 }).map((_, index) => (
                <SampleCardSkeleton key={index} />
              ))}
            </div>
          : <>
              {isZeroResults ?
                <div className="flex-1 pb-5 md:pb-10">
                  <header className="py-10 text-center md:py-8 lg:py-20">
                    <h1 className="mb-3 text-[28px] font-bold leading-[32px] md:mb-2 lg:mb-4">
                      {noResultsTitle}
                    </h1>
                    <p className="text-gray-500">{noResultsDescription}</p>
                  </header>
                  <SampleDepartmentList />
                </div>
              : <div>
                  <RefineSearchButtonsSimplified
                    facets={facetsForFilters}
                    query={samplesResults?.query ?? searchQuery}
                    filtersDefinition={filtersDefinition}
                    hitsCount={totalNbHits}
                    className="px-2.5 pb-2 md:sticky md:top-header-gap-unwrapped md:z-20 md:bg-white lg:px-8 lg:pb-4 lg:pt-5"
                  />
                  <div
                    key={uiKey}
                    className="grid h-fit grid-cols-2 gap-x-4 gap-y-4 px-2.5 md:grid-cols-3 md:gap-y-8 md:px-8 lg:grid-cols-4"
                  >
                    {collections.length >= 4 && (
                      <CollectionResultCarouselSimplified
                        className="col-span-full -mx-4 md:-mx-8"
                        collections={collections}
                        query={samplesResults?.query ?? searchQuery}
                        collectionNbHits={collectionResults?.nbHits ?? 0}
                        style={{
                          gridRowStart: 2,
                        }}
                      />
                    )}
                    {products.map((product, index) => (
                      <div
                        key={product.id}
                        ref={productRefs[index]}
                        data-product-id={product.id}
                        data-index={index + 1}
                        className="flex flex-col"
                      >
                        <SampleCard
                          {...product}
                          index={index}
                          where={DataLayerWhere.ProductCard}
                          item_list_id={ALGOLIA_SEARCH_INDEX}
                          item_list_name="Search results"
                          item_list_type="PLP"
                          feature={DataLayerFeature.Search}
                          showProductType={false}
                          hasSceneOpen={false}
                          viewMode="compact"
                          appliedFilters={applied_filters}
                        />
                      </div>
                    ))}
                    {products.length > 0 && !isLastPage && (
                      <div
                        ref={loadMoreRef}
                        className="col-span-full mt-8 flex h-10 items-center justify-center"
                        data-testid="search-load-more-sentinel"
                      >
                        <Spinner />
                      </div>
                    )}
                  </div>
                </div>
              }
            </>
          }
        </section>
      </div>
    </>
  );
}
